<script setup lang="ts">
import { ref, onUnmounted, watchEffect, CSSProperties } from "vue";
import { CSSAnimationName } from "@zyzgroup/core-common";
import {
  SupportedEaseNameToCubicBezier,
  animationEnd$,
  animationStart$
} from "@zyzgroup/core-web";

// for merge ce shadow-root styles
defineOptions({
  customOptions: {},
  inheritAttrs: true
});

export type CSSAnimationOptions = {
  name?: CSSAnimationName;
  timingFunction?: SupportedEaseNameToCubicBezier;
  duration?: number;
  delay?: number;
};

const props = withDefaults(
  defineProps<{
    name?: CSSAnimationName;
    timingFunction?: SupportedEaseNameToCubicBezier;
    // linear
    // ease
    // ease-in
    // ease-out
    // ease-in-out
    // step-start=steps(1, start) 动画执行时以开始端点为开始
    // step-end=steps(1, end) 动画执行时以结尾端点为开始
    // cubic-bezier(0.25, 0.1, 0.25, 1)
    // steps()
    duration?: number;
    delay?: number;
    repeat?: number | "infinite";
    direction?: "normal" | "reverse" | "alternate" | "alternate-reverse";
    fillMode?: "none" | "forwards" | "backwards" | "both";
    playState?: "running" | "paused";
    perspective?: string;
  }>(),
  {
    name: "jelly",
    delay: 0,
    fillMode: "both",
    playState: "running",
    perspective: "1200px"
  }
);

const emits = defineEmits<{
  (event: "cssanimationstart", e: AnimationEvent): void;
  (event: "cssanimationend", e: AnimationEvent): void;
}>();

const r = ref();

let s1;
let s2;
watchEffect(() => {
  if (!r.value || s1 || s2) {
    return;
  }
  s1 = animationStart$(r.value).subscribe((e) => {
    emits("cssanimationstart", e);
  });
  s2 = animationEnd$(r.value).subscribe((e) => {
    emits("cssanimationend", e);
  });
});

// 设置预定义变化
function getPredefinedStyle(options?: CSSAnimationOptions) {
  let defaultStyle: {
    duration?: number;
    timingFunction?: SupportedEaseNameToCubicBezier;
    repeat?: number | "infinite";
    direction?: "normal" | "reverse" | "alternate" | "alternate-reverse";
    transformOrigin?: string;
    parentOverflow?: string;
    backfaceVisibility?: "visible" | "hidden";
  } = {};
  switch (options?.name || props.name) {
    case "bounce":
      defaultStyle = {
        transformOrigin: "center bottom", // "50% 100%"
        parentOverflow: "visible"
      };
      break;
    case "bounceIn":
      defaultStyle = {
        duration: 750,
        parentOverflow: "visible"
      };
      break;
    case "bounceOut":
      defaultStyle = {
        duration: 750
      };
      break;
    case "fade":
      defaultStyle = {
        duration: 700
      };
      break;
    case "fadeIn":
      defaultStyle = {
        parentOverflow: "visible"
      };
      break;
    case "fadeOut":
      defaultStyle = {
        parentOverflow: "visible"
      };
      break;
    case "flip":
      defaultStyle = {
        backfaceVisibility: "visible",
        parentOverflow: "visible"
      };
      break;
    case "flash":
      defaultStyle = {
        parentOverflow: "visible"
      };
      break;
    case "flipInLeft":
      defaultStyle = {
        timingFunction: "ease-out"
      };
      break;
    case "flipInRight":
      defaultStyle = {
        timingFunction: "ease-out"
      };
      break;
    case "flipInBottom":
      defaultStyle = {
        timingFunction: "ease-out"
      };
      break;
    case "flipInTop":
      defaultStyle = {
        timingFunction: "ease-out"
      };
      break;
    case "flipInX":
      defaultStyle = {
        backfaceVisibility: "visible",
        parentOverflow: "visible"
      };
      break;
    case "flipInY":
      defaultStyle = {
        backfaceVisibility: "visible",
        parentOverflow: "visible"
      };
      break;
    case "flipOutBottom":
      defaultStyle = {
        timingFunction: "ease-in"
      };
      break;
    case "flipOutLeft":
      defaultStyle = {
        timingFunction: "ease-in"
      };
      break;
    case "flipOutRight":
      defaultStyle = {
        timingFunction: "ease-in"
      };
      break;
    case "flipOutTop":
      defaultStyle = {
        timingFunction: "ease-in"
      };
      break;
    case "flipOutX":
      defaultStyle = {
        duration: 750,
        backfaceVisibility: "visible",
        parentOverflow: "visible"
      };
      break;
    case "flipOutY":
      defaultStyle = {
        backfaceVisibility: "visible",
        parentOverflow: "visible"
      };
      break;
    case "headShake":
      defaultStyle = {
        timingFunction: "ease-in-out",
        parentOverflow: "visible"
      };
      break;
    case "heartBeat":
      defaultStyle = {
        duration: 1300,
        timingFunction: "ease-in-out",
        parentOverflow: "visible"
      };
      break;
    case "hinge":
      defaultStyle = {
        duration: 750,
        transformOrigin: "left top", // "0% 0%"
        parentOverflow: "visible"
      };
      break;
    case "jackInTheBox":
      defaultStyle = {
        parentOverflow: "visible"
      };
      break;
    case "jello":
      defaultStyle = {
        parentOverflow: "visible"
      };
      break;
    case "jelly":
      defaultStyle = {
        parentOverflow: "visible"
      };
      break;
    case "lightSpeedInLeft":
      defaultStyle = {
        timingFunction: "ease-out"
      };
      break;
    case "lightSpeedInRight":
      defaultStyle = {
        timingFunction: "ease-out"
      };
      break;
    case "lightSpeedOutLeft":
      defaultStyle = {
        timingFunction: "ease-in"
      };
      break;
    case "lightSpeedOutRight":
      defaultStyle = {
        timingFunction: "ease-in"
      };
      break;
    case "moveFromLeftFade":
      defaultStyle = {
        duration: 700
      };
      break;
    case "moveToLeftFade":
      defaultStyle = {
        duration: 700
      };
      break;
    case "moveFromRightFade":
      defaultStyle = {
        duration: 700
      };
      break;
    case "moveToRightFade":
      defaultStyle = {
        duration: 700
      };
      break;
    case "moveFromTopFade":
      defaultStyle = {
        duration: 700
      };
      break;
    case "moveToTopFade":
      defaultStyle = {
        duration: 700
      };
      break;
    case "moveFromBottomFade":
      defaultStyle = {
        duration: 700
      };
      break;
    case "moveToBottomFade":
      defaultStyle = {
        duration: 700
      };
      break;
    case "moveFromLeft":
      defaultStyle = {
        duration: 600
      };
      break;
    case "moveToLeft":
      defaultStyle = {
        duration: 600
      };
      break;
    case "moveFromRight":
      defaultStyle = {
        duration: 600
      };
      break;
    case "moveToRight":
      defaultStyle = {
        duration: 600
      };
      break;
    case "moveFromTop":
      defaultStyle = {
        duration: 600
      };
      break;
    case "moveToTop":
      defaultStyle = {
        duration: 600
      };
      break;
    case "moveFromBottom":
      defaultStyle = {
        duration: 600
      };
      break;
    case "moveToBottom":
      defaultStyle = {
        duration: 600
      };
      break;
    case "pulse":
      defaultStyle = {
        timingFunction: "ease-in-out",
        parentOverflow: "visible"
      };
      break;
    case "rotateFall":
      defaultStyle = {
        duration: 1000,
        timingFunction: "ease-in",
        transformOrigin: "left top" // "0% 0%"
      };
      break;
    case "rotateSlideOut":
      defaultStyle = {
        duration: 1000
      };
      break;
    case "rotateSlideIn":
      defaultStyle = {
        duration: 1000
      };
      break;
    case "rotateRightSideFirst":
      defaultStyle = {
        duration: 800,
        timingFunction: "ease-in",
        transformOrigin: "left center" // "0% 50%"
      };
      break;
    case "rotateLeftSideFirst":
      defaultStyle = {
        duration: 800,
        timingFunction: "ease-in",
        transformOrigin: "right center" // "100% 50%"
      };
      break;
    case "rotateTopSideFirst":
      defaultStyle = {
        duration: 800,
        timingFunction: "ease-in",
        transformOrigin: "center bottom" // "50% 100%"
      };
      break;
    case "rotateBottomSideFirst":
      defaultStyle = {
        duration: 800,
        timingFunction: "ease-in",
        transformOrigin: "center top" // "50% 0%"
      };
      break;
    case "rotatePushLeft":
      defaultStyle = {
        duration: 800,
        transformOrigin: "left center" // "0% 50%"
      };
      break;
    case "rotatePullBottom":
      defaultStyle = {
        transformOrigin: "center bottom" // "50% 100%"
      };
      break;
    case "rotatePullLeft":
      defaultStyle = {
        transformOrigin: "left center" // "0% 50%"
      };
      break;
    case "rotatePullRight":
      defaultStyle = {
        transformOrigin: "right center" // "100% 50%"
      };
      break;
    case "rotatePullTop":
      defaultStyle = {
        transformOrigin: "center top" // "50% 0%"
      };
      break;
    case "rotatePushRight":
      defaultStyle = {
        duration: 800,
        transformOrigin: "right center" // "100% 50%"
      };
      break;
    case "rotatePushTop":
      defaultStyle = {
        duration: 800,
        transformOrigin: "center top" // "50% 0%"
      };
      break;
    case "rotatePushBottom":
      defaultStyle = {
        duration: 800,
        transformOrigin: "center bottom" // "50% 100%"
      };
      break;
    case "rotateRoomLeftOut":
      defaultStyle = {
        duration: 800,
        transformOrigin: "right center" // "100% 50%"
      };
      break;
    case "rotateRoomLeftIn":
      defaultStyle = {
        duration: 800,
        transformOrigin: "left center" // "0% 50%"
      };
      break;
    case "rotateRoomRightOut":
      defaultStyle = {
        duration: 800,
        transformOrigin: "left center" // "0% 50%"
      };
      break;
    case "rotateRoomRightIn":
      defaultStyle = {
        duration: 800,
        transformOrigin: "right center" // "100% 50%"
      };
      break;
    case "rotateRoomTopOut":
      defaultStyle = {
        duration: 800,
        transformOrigin: "center bottom" // "50% 100%"
      };
      break;
    case "rotateRoomTopIn":
      defaultStyle = {
        duration: 800,
        transformOrigin: "center top" // "50% 0%"
      };
      break;
    case "rotateRoomBottomOut":
      defaultStyle = {
        duration: 800,
        transformOrigin: "center top" // "50% 0%"
      };
      break;
    case "rotateRoomBottomIn":
      defaultStyle = {
        duration: 800,
        transformOrigin: "center bottom" // "50% 100%"
      };
      break;
    case "rotateCarouselLeftOut":
      defaultStyle = {
        duration: 800
      };
      break;
    case "rotateCarouselLeftIn":
      defaultStyle = {
        duration: 800
      };
      break;
    case "rotateCarouselRightOut":
      defaultStyle = {
        duration: 800
      };
      break;
    case "rotateCarouselRightIn":
      defaultStyle = {
        duration: 800
      };
      break;
    case "rotateCarouselTopOut":
      defaultStyle = {
        duration: 800
      };
      break;
    case "rotateCarouselTopIn":
      defaultStyle = {
        duration: 800
      };
      break;
    case "rotateCarouselBottomOut":
      defaultStyle = {
        duration: 800
      };
      break;
    case "rotateCarouselBottomIn":
      defaultStyle = {
        duration: 800
      };
      break;
    case "rotateFoldRight":
      defaultStyle = {
        duration: 700,
        transformOrigin: "left center" // "0% 50%"
      };
      break;
    case "rotateFoldLeft":
      defaultStyle = {
        duration: 700,
        transformOrigin: "right center" // "100% 50%"
      };
      break;
    case "rotateFoldTop":
      defaultStyle = {
        duration: 700,
        transformOrigin: "center bottom" // "50% 100%"
      };
      break;
    case "rotateFoldBottom":
      defaultStyle = {
        duration: 700,
        transformOrigin: "center top" // "50% 0%"
      };
      break;
    case "rotateUnfoldLeft":
      defaultStyle = {
        duration: 700,
        transformOrigin: "right center" // "100% 50%"
      };
      break;
    case "rotateUnfoldRight":
      defaultStyle = {
        duration: 700,
        transformOrigin: "left center" // "0% 50%"
      };
      break;
    case "rotateUnfoldTop":
      defaultStyle = {
        duration: 700,
        transformOrigin: "center bottom" // "50% 100%"
      };
      break;
    case "rotateUnfoldBottom":
      defaultStyle = {
        duration: 700,
        transformOrigin: "center top" // "50% 0%"
      };
      break;
    case "rotateCubeLeftOut":
      defaultStyle = {
        duration: 600,
        timingFunction: "ease-in",
        transformOrigin: "right center" // "100% 50%"
      };
      break;
    case "rotateCubeLeftIn":
      defaultStyle = {
        duration: 600,
        timingFunction: "ease-in",
        transformOrigin: "left center" // "0% 50%"
      };
      break;
    case "rotateCubeRightOut":
      defaultStyle = {
        duration: 600,
        timingFunction: "ease-in",
        transformOrigin: "left center" // "0% 50%"
      };
      break;
    case "rotateCubeRightIn":
      defaultStyle = {
        duration: 600,
        timingFunction: "ease-in",
        transformOrigin: "right center" // "100% 50%"
      };
      break;
    case "rotateCubeTopOut":
      defaultStyle = {
        duration: 600,
        timingFunction: "ease-in",
        transformOrigin: "center bottom" // "50% 100%"
      };
      break;
    case "rotateCubeTopIn":
      defaultStyle = {
        duration: 600,
        timingFunction: "ease-in",
        transformOrigin: "center top" // "50% 0%"
      };
      break;
    case "rotateCubeBottomOut":
      defaultStyle = {
        duration: 600,
        timingFunction: "ease-in",
        transformOrigin: "center top" // "50% 0%"
      };
      break;
    case "rotateCubeBottomIn":
      defaultStyle = {
        duration: 600,
        timingFunction: "ease-in",
        transformOrigin: "center bottom" // "50% 100%"
      };
      break;
    case "rotateInNewspaper":
      defaultStyle = {
        timingFunction: "ease-out"
      };
      break;
    case "rotateOutNewspaper":
      defaultStyle = {
        timingFunction: "ease-in"
      };
      break;
    case "rotateInUpLeft":
      defaultStyle = {
        transformOrigin: "left bottom" // "0% 100%"
      };
      break;
    case "rotateInUpRight":
      defaultStyle = {
        transformOrigin: "right bottom" // "100% 100%"
      };
      break;
    case "rotateInDownLeft":
      defaultStyle = {
        transformOrigin: "left bottom" // "0% 100%"
      };
      break;
    case "rotateInDownRight":
      defaultStyle = {
        transformOrigin: "right bottom" // "100% 100%"
      };
      break;
    case "rotateOutUpLeft":
      defaultStyle = {
        transformOrigin: "left bottom" // "0% 100%"
      };
      break;
    case "rotateOutUpRight":
      defaultStyle = {
        transformOrigin: "right bottom" // "100% 100%"
      };
      break;
    case "rotateOutDownLeft":
      defaultStyle = {
        transformOrigin: "left bottom" // "0% 100%"
      };
      break;
    case "rotateOutDownRight":
      defaultStyle = {
        transformOrigin: "right bottom" // "100% 100%"
      };
      break;
    case "rotateSidesIn":
      defaultStyle = {
        timingFunction: "ease-out"
      };
      break;
    case "rotateSidesOut":
      defaultStyle = {
        timingFunction: "ease-in"
      };
      break;
    case "rubberBand":
      defaultStyle = {
        parentOverflow: "visible"
      };
      break;
    case "shakeX":
      defaultStyle = {
        parentOverflow: "visible"
      };
      break;
    case "shakeY":
      defaultStyle = {
        parentOverflow: "visible"
      };
      break;
    case "statusProcessing":
      defaultStyle = {
        duration: 1200,
        timingFunction: "ease-in-out",
        repeat: "infinite"
      };
      break;
    case "superRainbow":
      defaultStyle = {
        timingFunction: "linear",
        duration: 15000,
        repeat: "infinite",
        direction: "alternate"
      };
      break;
    case "swing":
      defaultStyle = {
        transformOrigin: "center top", // "50% 0%"
        parentOverflow: "visible"
      };
      break;
    case "tada":
      defaultStyle = {
        parentOverflow: "visible"
      };
      break;
    case "wobble":
      defaultStyle = {
        parentOverflow: "visible"
      };
      break;
    case "zoomOutRight":
      defaultStyle = {
        transformOrigin: "right center" // "100% 50%"
      };
      break;
    case "zoomOutDown":
      defaultStyle = {
        transformOrigin: "center bottom" // "50% 100%"
      };
      break;
    case "zoomOutLeft":
      defaultStyle = {
        transformOrigin: "left center" // "0% 50%"
      };
      break;
    case "zoomOutUp":
      defaultStyle = {
        transformOrigin: "center top" // "50% 0%"
      };
      break;
  }
  return {
    duration:
      options?.duration ?? props.duration ?? defaultStyle.duration ?? 500,
    timingFunction:
      options?.timingFunction ??
      props.timingFunction ??
      defaultStyle.timingFunction ??
      "ease",
    repeat: props.repeat ?? defaultStyle.repeat ?? 1,
    direction: props.direction ?? defaultStyle.direction ?? "normal",
    transformOrigin: defaultStyle.transformOrigin ?? "50% 50%", // [x-offset y-offset] "center" or "50% 50%"
    backfaceVisibility: defaultStyle.backfaceVisibility ?? "hidden", // visible hidden
    parentOverflow: defaultStyle.parentOverflow ?? "hidden"
  };
}

function getBasicStyle(options?: CSSAnimationOptions) {
  return {
    transformStyle: "preserve-3d"
    // transform: "translate3d(0, 0, 0)", // Chrome Layerize 花费时间长
    // perspective: props.perspective
    // willChange: "box-shadow, transform",
  } as CSSProperties;
}

const style = ref<CSSProperties>({});

// 清除
const clear = (options?: CSSAnimationOptions) => {
  style.value = {
    ...getBasicStyle(options),
    animation: "none"
  };
  if (r.value) {
    // restart css animation
    r.value.style.animation = "none";
    r.value.offsetHeight; // trigger reflow
    r.value.style.animation = null;
  }
};
// 初始化执行
clear();

function set(
  options?: CSSAnimationOptions,
  forceDirection?: "normal" | "reverse" | "alternate" | "alternate-reverse"
) {
  const predefinedStyle = getPredefinedStyle(options);
  const duration = predefinedStyle.duration;
  const timingFunction = predefinedStyle.timingFunction;
  const repeat = predefinedStyle.repeat;
  let direction = predefinedStyle.direction;
  if (forceDirection) {
    direction = forceDirection;
  }
  const delay =
    typeof options?.delay == "number" ? options?.delay : props.delay;
  const transformOrigin = predefinedStyle.transformOrigin;
  const backfaceVisibility = predefinedStyle.backfaceVisibility;
  const parentOverflow = predefinedStyle.parentOverflow;
  if (r.value && r.value.parentElement) {
    r.value.parentElement.style.overflow = parentOverflow;
  }

  // animation: moveObject 2s ease-in-out, fadeBox 3s linear;
  // animation-name: moveObject, fadeBox;
  // animation-duation: 2s, 3s;
  // animation-timing-function: ease-in-out, linear;

  // .exmaple {
  //   animation-play-state: paused;
  // }
  // .exmaple:hover {
  //   animation-play-state: running;
  // }

  style.value = {
    ...getBasicStyle(options),
    // animation: `${animationName} ${duration}ms ${timingFunction} ${delay}ms ${repeat} ${direction} ${props.fillMode} ${props.playState}`,
    animationName: options?.name || props.name,
    animationDuration: `${duration}ms`,
    animationDelay: `${delay}ms`,
    animationIterationCount: repeat,
    animationFillMode: props.fillMode,
    animationTimingFunction: timingFunction,
    animationDirection: direction,
    animationPlayState: props.playState,
    transformOrigin,
    backfaceVisibility
  };
}

// css animation 与 js animation 有时间差
// 统一用 css animation ，抹平与 js animation 的差异
function trigger(options?: CSSAnimationOptions) {
  clear(options);
  set(options, "normal");
}

function triggerReverse(options?: CSSAnimationOptions) {
  clear(options);
  set(options, "reverse");
}

onUnmounted(() => {
  s1?.unsubscribe();
  s2?.unsubscribe();
});

defineExpose({ trigger, triggerReverse, clear });
</script>

<template>
  <div
    ref="r"
    class="css-animation"
    :style="style"
  >
    <slot></slot>
  </div>
</template>

<!-- 放在顶层 main.ts 中以节省空间 -->
<!-- <style>
@import "../../../../../css/animate_ce.css";
</style> -->
